import { useState, useEffect } from 'react';
import SessionDetailForm from './form/SessionDetailForm';
import {
	useGetFeedbackServicesQuery,
	useLazyGetFeedbackUserDetailsQuery,
	useLazyGetProgramGroupsQuery,
	useLazyGetProgramQuestionsQuery,
} from '../../store/api/feedbackApi';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../reducers';
import { useGetCurrentUserQuery } from '../../store/api/userApi';
import { loggedInUserDetails } from '../../actions/loggedInUserDetailsActions';
import SessionFeedbackForm from './form/SessionFeedbackForm';
import SessionSummary from './SessionSummary';
import { feedbackSessionFormInFocus } from '../../actions/feedbackFormInFocusAction';
import { feedbackSessionQuestion } from '../../actions/feedbackQuestionAction';
import { updateSessionDetailForm, updateSessionFeedbackForm } from '../../actions/feedbackAnswerAction';
import _ from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { trackMixpanelEvent } from '../../services/mixpanel';

interface ProgramsDetailsInterface {
	id: number;
	full_name: string;
	profile_photo_url: string;
	programs: { id: number; name: string; description: string }[];
	user_type: string;
}

interface ServicesDetailsInterface {
	id: number;
	service_name: string;
	label: string;
	value: number;
}

export interface GroupDetailsInterface {
	group_id: number;
	mentee: { id: number; full_name: string; profile_photo: string };
	mentor: { id: number; full_name: string; profile_photo: string };
}

export interface ProgramQuestionInterface {
	active: boolean;
	category: string;
	checkboxes: string[];
	created_at: string;
	description: string;
	enterprise_id: number | null;
	id: number;
	options: string[];
	primary: boolean;
	primary_tag: any;
	program_id: number;
	required: boolean;
	stars: number;
	title: string;
	updated_at: string;
}

export interface SessionFeedbackAnswerInterface {
	sessionDetailForm: SessionDetailFormInterface;
	sessionFeedbackForm: SessionFeedbackFormInterface;
}

export interface SessionDetailFormInterface {
	bookingId: number;
	ratedParticipantId: number | undefined;
	programme: { label: string; value: number };
	sessionWith: { label: string; value: { group_id: number; mentee: { id: number; full_name: string }; mentor: { id: number; full_name: string } } };
	sessionType: number;
	sessionDate: Date | null;
	sessionTime: Date | null;
	sessionDuration: { label: string; value: number };
	sessionService: { label: string; value: number };
}

export interface SessionFeedbackFormInterface {
	submitted: string;
	feedback: { question_id: number; question_category: string; text: string | string[] | null; value: number | null }[];
}

export default function SessionFeedback() {
	const methods = useForm();
	const loggedInUserDetail = useSelector((state: RootState) => state.loggedInUserDetails);
	const currentFormInFocus = useSelector((state: RootState) => state.feedbackFormInFocus);
	const programQuestion = useSelector((state: RootState) => state.programQuestion);
	const programAnswer = useSelector((state: RootState) => state.programAnswer);
	const [programsDetails, setProgramsDetails] = useState<ProgramsDetailsInterface>();
	const [groupDetails, setGroupDetails] = useState<GroupDetailsInterface[]>([]);
	const dispatch = useDispatch();
	const { data: servicesDetails = [] } = useGetFeedbackServicesQuery();
	const [loadFeedbackUserDetails] = useLazyGetFeedbackUserDetailsQuery();
	const [loadProgramGroups] = useLazyGetProgramGroupsQuery();
	const [loadProgramQuestions] = useLazyGetProgramQuestionsQuery();
	const { data: currentUser } = useGetCurrentUserQuery(undefined, { skip: !_.isEmpty(loggedInUserDetail) });

	useEffect(() => {
		if (currentFormInFocus === null) {
			dispatch(feedbackSessionFormInFocus(1));
		}
	}, [currentFormInFocus, dispatch]);

	useEffect(() => {
		if (_.isEmpty(loggedInUserDetail) && currentFormInFocus === 1 && currentUser) {
			dispatch(loggedInUserDetails(currentUser));
		}
	}, [currentFormInFocus, currentUser, dispatch, loggedInUserDetail]);

	useEffect(() => {
		if (_.isEmpty(programsDetails) && loggedInUserDetail?.id && currentFormInFocus === 1) {
			loadFeedbackUserDetails(loggedInUserDetail.id)
				.unwrap()
				.then(setProgramsDetails)
				.catch((error) => console.warn('Unable to load feedback programs:', error));
		}
	}, [currentFormInFocus, loadFeedbackUserDetails, loggedInUserDetail?.id, programsDetails]);

	const handleProgrammeChange = (programId: number) => {
		if (programId) {
			loadProgramGroups(programId)
				.unwrap()
				.then((groups) =>
					setGroupDetails(
						groups.filter((group) => group.mentee.id === loggedInUserDetail.id || group.mentor.id === loggedInUserDetail.id)
					)
				)
				.catch((error) => console.warn('Unable to load feedback groups:', error));
		}
	};

	const groupData = groupDetails.map((groupDetail) => ({ label: groupDetail.mentee.id !== loggedInUserDetail.id ? groupDetail.mentee.full_name : groupDetail.mentor.full_name, value: groupDetail }));
	const programData = programsDetails?.programs.map((program) => ({ label: program.name, value: program.id }));
	const serviceData = servicesDetails.map((serviceDetails) => ({ label: serviceDetails.label, value: serviceDetails.value }));
	const sessionDurationData = [{ durationName: '30 minutes', durationValue: 30 }, { durationName: 'an hour', durationValue: 60 }, { durationName: 'an hour 30 minutes', durationValue: 90 }, { durationName: '2 hours', durationValue: 120 }].map((time) => ({ label: time.durationName, value: time.durationValue }));

	const handleSubmitSessionDetail = async (sessionDetails: SessionDetailFormInterface) => {
		try {
			const userRole = sessionDetails.sessionWith.value.mentee.id !== loggedInUserDetail.id ? 'mentee' : 'mentor';
			await dispatch(updateSessionDetailForm(sessionDetails));
			const questions = await loadProgramQuestions({ programId: sessionDetails.programme.value, userType: userRole }).unwrap();
			dispatch(feedbackSessionQuestion(questions));
			await dispatch(feedbackSessionFormInFocus(2));
		} catch (error) {
			console.log('error: ', error);
		}
		trackMixpanelEvent('Filled Session Details');
	};

	const handleSubmitSessionFeedback = (sessionFeedback: SessionFeedbackFormInterface) => {
		dispatch(updateSessionFeedbackForm(sessionFeedback));
		dispatch(feedbackSessionFormInFocus(3));
		trackMixpanelEvent('Filled Session Feedback Form');
	};

	return (
		<FormProvider {...methods}>
			<div>
				{currentFormInFocus === 1 && <SessionDetailForm groupData={groupData} handleProgrammeChange={handleProgrammeChange} onSubmit={handleSubmitSessionDetail} programData={programData} serviceData={serviceData} sessionDurationData={sessionDurationData} />}
				{currentFormInFocus === 2 && <SessionFeedbackForm onSubmit={handleSubmitSessionFeedback} />}
				{currentFormInFocus === 3 && <SessionSummary loggedInUserDetail={loggedInUserDetail} programAnswer={programAnswer} programQuestion={programQuestion} />}
			</div>
		</FormProvider>
	);
}
